import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/LanguageContext";
import { resolveImageUrl } from "@/lib/media";
import { motion } from "framer-motion";
import { CalendarDays, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso, lang) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "id" ? "id-ID" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const News = () => {
  const { t, lang } = useLanguage();
  const [news, setNews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/news`)
      .then((res) => setNews(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        console.error("Failed to load news:", e);
        setNews([]);
      });
  }, []);

  return (
    <section
      id="news"
      data-testid="news-section"
      className="section-y bg-blue-50/60"
    >
      <div className="container-x">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-4">{t.news.overline}</div>
          <h2
            data-testid="news-title"
            className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
          >
            {t.news.title}
          </h2>
          <p className="text-slate-600 leading-relaxed">{t.news.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {news.map((n, i) => (
            <motion.article
              key={n.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`news-card-${n.id}`}
              className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5 hover:-translate-y-1 transition-all duration-500 flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={resolveImageUrl(n.image)}
                  alt={lang === "id" ? n.title_id : n.title_en}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider text-blue-800">
                  {lang === "id" ? n.category_id : n.category_en}
                </div>
              </div>

              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-2">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {formatDate(n.date, lang)}
                </div>
                <h3 className="font-heading text-base font-semibold text-slate-900 leading-snug mb-2 line-clamp-2">
                  {lang === "id" ? n.title_id : n.title_en}
                </h3>
                <p className="text-sm text-slate-600 line-clamp-3 mb-4 flex-1">
                  {lang === "id" ? n.excerpt_id : n.excerpt_en}
                </p>
                <Link
                  to={`/berita/${n.id}`}
                  data-testid={`news-read-${n.id}`}
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-800 hover:text-blue-900 self-start"
                >
                  {t.news.read_more}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default News;
